import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../state/AuthContext";
import { PageHeader } from "../components/RequestRouting";
import { StoriesTabStrip } from "../components/StoriesTabStrip";
import { LOCAL_SEED } from "../data/localSeed";
import {
  loadEmploymentStatusMap,
  saveEmploymentStatusMap,
  SEED_BURDEN_ASSIGNMENTS,
  SEED_POLICIES,
} from "../data/timekeepingSeed";
import {
  SEED_COMPANY_FY,
  perPeriodAccrual,
  type LeaveAccrualBand,
} from "../data/leaveSeed";
import { useLeaveData } from "../hooks/useLeaveApi";
import { DEMO_EMPLOYEE } from "../lib/roles";
import {
  EMPLOYMENT_STATUS_HELP,
  EMPLOYMENT_STATUSES,
  policyForDate,
  requiredHoursForPeriod,
  type CompanyTimekeepingPolicy,
  type EmploymentStatus,
  type HourBasis,
  type PeriodCadence,
} from "../lib/timekeeping";
import {
  mergeFederal,
  seedFederalHolidays,
  type CompanyHoliday,
  type HolidayCalendarMode,
} from "../lib/holidays";

function cadenceOk(cadence: PeriodCadence, basis: HourBasis): boolean {
  if (cadence === "weekly" && basis === "80_per_biweekly") return false;
  if (cadence === "biweekly" && basis === "40_per_week") return false;
  return true;
}

type BurdenRow = (typeof SEED_BURDEN_ASSIGNMENTS)[number];

export function CompanyAdminPage() {
  const { moduleIds, homePath } = useAuth();
  const [tab, setTab] = useState<"policy" | "burden" | "leave" | "holidays">("policy");
  const [holidayMode, setHolidayMode] = useState<HolidayCalendarMode>("inherit_federal");
  const [holidays, setHolidays] = useState<CompanyHoliday[]>(() =>
    seedFederalHolidays([2026, 2027]),
  );
  const [holidayMsg, setHolidayMsg] = useState<string | null>(null);
  const [newHol, setNewHol] = useState({ date: "", name: "" });
  const { policy: leavePolicy, fy, waking, source, persistPolicy, persistFy } = useLeaveData();
  const [leaveMsg, setLeaveMsg] = useState<string | null>(null);
  const [bandForm, setBandForm] = useState({ min_years: "0", annual_hours: "120" });
  const [carryForm, setCarryForm] = useState({ max_hours: "40", effective_date: "" });
  const [fyForm, setFyForm] = useState({
    fy_start_month: String(SEED_COMPANY_FY.fy_start_month),
    fy_start_day: String(SEED_COMPANY_FY.fy_start_day),
  });

  useEffect(() => {
    setFyForm({
      fy_start_month: String(fy.fy_start_month),
      fy_start_day: String(fy.fy_start_day),
    });
  }, [fy.fy_start_month, fy.fy_start_day]);
  const [burdens, setBurdens] = useState<BurdenRow[]>(() =>
    SEED_BURDEN_ASSIGNMENTS.map((b) => ({ ...b })),
  );
  const [policies, setPolicies] = useState<CompanyTimekeepingPolicy[]>(() =>
    SEED_POLICIES.map((p) => ({ ...p })),
  );
  const [companyFilter, setCompanyFilter] = useState<string>("co-hfs");
  const [form, setForm] = useState({
    company_id: "co-hfs",
    company_name: "Harbor Federal Services",
    cadence: "biweekly" as PeriodCadence,
    hour_basis: "80_per_biweekly" as HourBasis,
    effective_date: "",
    end_date: "",
  });
  const [burdenForm, setBurdenForm] = useState({
    employee_id: DEMO_EMPLOYEE.id,
    structure_name: "HFS Standard Fringe + OH",
    effective_date: "",
    end_date: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [burdenMsg, setBurdenMsg] = useState<string | null>(null);
  const [statusMap, setStatusMap] = useState<Record<string, EmploymentStatus>>(() =>
    loadEmploymentStatusMap(),
  );
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  const companies = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of policies) map.set(p.company_id, p.company_name);
    return [...map.entries()];
  }, [policies]);

  const visible = useMemo(
    () =>
      policies
        .filter((p) => p.company_id === companyFilter)
        .sort((a, b) => b.effective_date.localeCompare(a.effective_date)),
    [policies, companyFilter],
  );

  const employeeLabels: Record<string, string> = {
    [DEMO_EMPLOYEE.id]: DEMO_EMPLOYEE.name,
    "emp-ortiz": "Jordan Ortiz",
    "emp-blake": "Morgan Blake",
    "emp-4": "Riley Nguyen",
    "emp-patel": "Sam Patel",
  };

  const companyEmployees = useMemo(
    () =>
      LOCAL_SEED.employees.filter((e) => String(e.company_id) === companyFilter),
    [companyFilter],
  );

  const activePolicy = useMemo(
    () => policyForDate(policies, companyFilter, "2026-07-27"),
    [policies, companyFilter],
  );

  function setEmployeeStatus(employeeId: string, status: EmploymentStatus) {
    setStatusMap((prev) => {
      const next = { ...prev, [employeeId]: status };
      saveEmploymentStatusMap(next);
      return next;
    });
    setStatusMsg(`Saved ${employeeLabels[employeeId] ?? employeeId} → ${status}.`);
  }

  if (!moduleIds.includes("company-admin")) {
    return <Navigate to={homePath} replace />;
  }

  function onCompanyPick(id: string) {
    setCompanyFilter(id);
    const name = companies.find(([c]) => c === id)?.[1] ?? id;
    setForm((f) => ({ ...f, company_id: id, company_name: name }));
  }

  function addPolicy(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(null);
    if (!form.effective_date) {
      setError("Effective date is required.");
      return;
    }
    if (!cadenceOk(form.cadence, form.hour_basis)) {
      setError(
        "Cadence and hour basis must align (weekly ↔ 40/week, biweekly ↔ 80/biweekly).",
      );
      return;
    }
    const end = form.end_date.trim() || null;
    if (end && end < form.effective_date) {
      setError("End date cannot be before effective date.");
      return;
    }

    setPolicies((prev) => {
      const next = prev.map((p) => {
        if (
          p.company_id === form.company_id &&
          p.end_date == null &&
          p.effective_date < form.effective_date
        ) {
          const dayBefore = new Date(form.effective_date + "T12:00:00");
          dayBefore.setDate(dayBefore.getDate() - 1);
          const y = dayBefore.getFullYear();
          const m = String(dayBefore.getMonth() + 1).padStart(2, "0");
          const d = String(dayBefore.getDate()).padStart(2, "0");
          return { ...p, end_date: `${y}-${m}-${d}` };
        }
        return p;
      });
      return [
        {
          id: `pol-${Date.now()}`,
          company_id: form.company_id,
          company_name: form.company_name,
          cadence: form.cadence,
          hour_basis: form.hour_basis,
          hour_increment: 0.25,
          effective_date: form.effective_date,
          end_date: end,
        },
        ...next,
      ];
    });
    setSaved("Policy saved for this company only. Other companies are unchanged.");
    setForm((f) => ({ ...f, effective_date: "", end_date: "" }));
  }

  function addBurden(e: FormEvent) {
    e.preventDefault();
    setBurdenMsg(null);
    if (!burdenForm.effective_date) {
      setBurdenMsg("Effective date is required.");
      return;
    }
    const end = burdenForm.end_date.trim() || null;
    if (end && end < burdenForm.effective_date) {
      setBurdenMsg("End date cannot be before effective date.");
      return;
    }
    setBurdens((prev) => {
      const closed = prev.map((b) => {
        if (
          b.employee_id === burdenForm.employee_id &&
          b.end_date == null &&
          b.effective_date < burdenForm.effective_date
        ) {
          const dayBefore = new Date(burdenForm.effective_date + "T12:00:00");
          dayBefore.setDate(dayBefore.getDate() - 1);
          const y = dayBefore.getFullYear();
          const m = String(dayBefore.getMonth() + 1).padStart(2, "0");
          const d = String(dayBefore.getDate()).padStart(2, "0");
          return { ...b, end_date: `${y}-${m}-${d}` };
        }
        return b;
      });
      return [
        {
          id: `bur-${Date.now()}`,
          employee_id: burdenForm.employee_id,
          structure_name: burdenForm.structure_name,
          effective_date: burdenForm.effective_date,
          end_date: end,
        },
        ...closed,
      ];
    });
    setBurdenMsg("Burden assignment saved with effective dates.");
    setBurdenForm((f) => ({ ...f, effective_date: "", end_date: "" }));
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Company admin"
        subtitle="Per-company operating policy and employee burden with effective dates. Changing one company never changes another."
      />

      <StoriesTabStrip
        tabs={[
          { id: "policy", label: "Timesheet policy" },
          { id: "burden", label: "Employee burden" },
          { id: "leave", label: "Leave policy" },
          { id: "holidays", label: "Holidays" },
        ]}
        active={tab}
        onChange={(id) => setTab(id as typeof tab)}
      />

      {tab === "policy" && (
        <>
          <div className="flex flex-wrap gap-2">
            {companies.map(([id, name]) => (
              <button
                key={id}
                type="button"
                onClick={() => onCompanyPick(id)}
                className={`px-3 py-1.5 text-sm ${
                  companyFilter === id
                    ? "bg-navy text-white"
                    : "bg-white border border-black/10 text-navy"
                }`}
              >
                {name}
              </button>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-white border border-black/5 overflow-hidden">
              <div className="px-4 py-3 border-b border-black/5 font-semibold text-navy">
                Timekeeping policies
              </div>
              <table className="w-full text-sm">
                <thead className="bg-mist">
                  <tr>
                    <th className="text-left px-3 py-2 text-slate">Effective</th>
                    <th className="text-left px-3 py-2 text-slate">End</th>
                    <th className="text-left px-3 py-2 text-slate">Cadence</th>
                    <th className="text-left px-3 py-2 text-slate">Hour basis</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((p) => (
                    <tr key={p.id} className="border-t border-black/5">
                      <td className="px-3 py-2">{p.effective_date}</td>
                      <td className="px-3 py-2">{p.end_date ?? "—"}</td>
                      <td className="px-3 py-2 capitalize">{p.cadence}</td>
                      <td className="px-3 py-2">
                        {p.hour_basis === "40_per_week"
                          ? "40 hrs / week"
                          : "80 hrs / biweekly"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="px-4 py-3 text-xs text-slate border-t border-black/5">
                Current periods use the policy effective as of the period start. Harbor Federal has
                an upcoming switch to weekly / 40 on 2026-10-01.
              </p>
            </div>

            <form onSubmit={addPolicy} className="bg-white border border-black/5 p-4 space-y-3">
              <h2 className="font-semibold text-navy">Add / supersede policy</h2>
              <p className="text-xs text-slate">
                Applies only to <strong>{form.company_name}</strong>. Hour increment is fixed at
                0.25 for this demo. Required hours = full-time basis × employment status
                (FT=100%, PT20=50%, PT32=80%, PTV=no minimum). Employees may enter more than the
                minimum, but not less.
              </p>
              <label className="block text-sm font-medium text-navy">
                Period cadence
                <select
                  className="mt-1 w-full border border-black/10 px-3 py-2"
                  value={form.cadence}
                  onChange={(e) => {
                    const cadence = e.target.value as PeriodCadence;
                    setForm((f) => ({
                      ...f,
                      cadence,
                      hour_basis: cadence === "weekly" ? "40_per_week" : "80_per_biweekly",
                    }));
                  }}
                >
                  <option value="weekly">Every 1 week</option>
                  <option value="biweekly">Every 2 weeks</option>
                </select>
              </label>
              <label className="block text-sm font-medium text-navy">
                Full-time hour floor
                <select
                  className="mt-1 w-full border border-black/10 px-3 py-2"
                  value={form.hour_basis}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, hour_basis: e.target.value as HourBasis }))
                  }
                >
                  <option value="40_per_week">40 hours every week</option>
                  <option value="80_per_biweekly">80 hours every two weeks</option>
                </select>
              </label>
              <label className="block text-sm font-medium text-navy">
                Effective date
                <input
                  type="date"
                  required
                  className="mt-1 w-full border border-black/10 px-3 py-2"
                  value={form.effective_date}
                  onChange={(e) => setForm((f) => ({ ...f, effective_date: e.target.value }))}
                />
              </label>
              <label className="block text-sm font-medium text-navy">
                End date (optional)
                <input
                  type="date"
                  className="mt-1 w-full border border-black/10 px-3 py-2"
                  value={form.end_date}
                  onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                />
              </label>
              {error && <p className="text-sm text-red-700">{error}</p>}
              {saved && <p className="text-sm text-teal">{saved}</p>}
              <button type="submit" className="px-4 py-2 bg-navy text-white text-sm">
                Save policy
              </button>
            </form>
          </div>

          <div className="bg-white border border-black/5 overflow-hidden">
            <div className="px-4 py-3 border-b border-black/5 font-semibold text-navy">
              Employment status → period minimum
            </div>
            <p className="px-4 py-2 text-xs text-slate border-b border-black/5">
              Demo control for HR employment_status (DEC-TIME-002). Changing Avery to PT20 drops
              her timesheet minimum to half the company basis. HR owns this field in production;
              Company Admin can demonstrate floors here.
            </p>
            <table className="w-full text-sm">
              <thead className="bg-mist">
                <tr>
                  <th className="text-left px-3 py-2 text-slate">Employee</th>
                  <th className="text-left px-3 py-2 text-slate">Status</th>
                  <th className="text-left px-3 py-2 text-slate">Period minimum</th>
                </tr>
              </thead>
              <tbody>
                {companyEmployees.map((e) => {
                  const id = String(e.id);
                  const st = statusMap[id] ?? "FT";
                  const min = activePolicy
                    ? requiredHoursForPeriod(activePolicy, st)
                    : null;
                  return (
                    <tr key={id} className="border-t border-black/5">
                      <td className="px-3 py-2 text-navy">{String(e.name)}</td>
                      <td className="px-3 py-2">
                        <select
                          className="border border-black/10 px-2 py-1 text-sm"
                          value={st}
                          onChange={(ev) =>
                            setEmployeeStatus(id, ev.target.value as EmploymentStatus)
                          }
                          title={EMPLOYMENT_STATUS_HELP[st].meaning}
                        >
                          {EMPLOYMENT_STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {s} — {EMPLOYMENT_STATUS_HELP[s].label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-slate">
                        {min == null ? "None (PTV)" : `${min} hrs`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {statusMsg && (
              <p className="px-4 py-2 text-sm text-teal border-t border-black/5">{statusMsg}</p>
            )}
          </div>

          <div className="bg-white border border-black/5 p-4 text-sm text-slate space-y-2">
            <h2 className="font-semibold text-navy">Role boundary</h2>
            <p>
              Company Admin owns per-company operating settings (timesheet cadence, hour floors,
              employment-status floor demo, employee burden). HR owns setting employment_status on
              the employee master. Security / Integration owns IdP, SSO, APIs, and platform audit —
              not how this company runs its timesheet calendar.
            </p>
          </div>
        </>
      )}

      {tab === "burden" && (
        <div className="grid lg:grid-cols-2 gap-4">
          <div className="bg-white border border-black/5 overflow-hidden">
            <div className="px-4 py-3 border-b border-black/5 font-semibold text-navy">
              EmployeeBurdenAssignment
            </div>
            <table className="w-full text-sm">
              <thead className="bg-mist">
                <tr>
                  <th className="text-left px-3 py-2 text-slate">Employee</th>
                  <th className="text-left px-3 py-2 text-slate">Structure</th>
                  <th className="text-left px-3 py-2 text-slate">Effective</th>
                  <th className="text-left px-3 py-2 text-slate">End</th>
                </tr>
              </thead>
              <tbody>
                {burdens.map((b) => (
                  <tr key={b.id} className="border-t border-black/5">
                    <td className="px-3 py-2">
                      {employeeLabels[b.employee_id] ?? b.employee_id}
                    </td>
                    <td className="px-3 py-2">{b.structure_name}</td>
                    <td className="px-3 py-2">{b.effective_date}</td>
                    <td className="px-3 py-2">{b.end_date ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="px-4 py-3 text-xs text-slate border-t border-black/5">
              Overhead / fringe structures are assigned per person with start and end dates. Open
              historical periods keep the structure that was effective as of period start.
            </p>
          </div>

          <form onSubmit={addBurden} className="bg-white border border-black/5 p-4 space-y-3">
            <h2 className="font-semibold text-navy">Add / supersede burden</h2>
            <label className="block text-sm font-medium text-navy">
              Employee
              <select
                className="mt-1 w-full border border-black/10 px-3 py-2"
                value={burdenForm.employee_id}
                onChange={(e) =>
                  setBurdenForm((f) => ({ ...f, employee_id: e.target.value }))
                }
              >
                <option value={DEMO_EMPLOYEE.id}>{DEMO_EMPLOYEE.name}</option>
                <option value="emp-ortiz">Jordan Ortiz</option>
                <option value="emp-blake">Morgan Blake</option>
              </select>
            </label>
            <label className="block text-sm font-medium text-navy">
              Structure
              <select
                className="mt-1 w-full border border-black/10 px-3 py-2"
                value={burdenForm.structure_name}
                onChange={(e) =>
                  setBurdenForm((f) => ({ ...f, structure_name: e.target.value }))
                }
              >
                <option>HFS Standard Fringe + OH</option>
                <option>HFS PM Fringe + OH</option>
                <option>River Labs Light OH</option>
              </select>
            </label>
            <label className="block text-sm font-medium text-navy">
              Effective date
              <input
                type="date"
                required
                className="mt-1 w-full border border-black/10 px-3 py-2"
                value={burdenForm.effective_date}
                onChange={(e) =>
                  setBurdenForm((f) => ({ ...f, effective_date: e.target.value }))
                }
              />
            </label>
            <label className="block text-sm font-medium text-navy">
              End date (optional)
              <input
                type="date"
                className="mt-1 w-full border border-black/10 px-3 py-2"
                value={burdenForm.end_date}
                onChange={(e) =>
                  setBurdenForm((f) => ({ ...f, end_date: e.target.value }))
                }
              />
            </label>
            {burdenMsg && (
              <p
                className={`text-sm ${
                  burdenMsg.includes("required") || burdenMsg.includes("cannot")
                    ? "text-red-700"
                    : "text-teal"
                }`}
              >
                {burdenMsg}
              </p>
            )}
            <button type="submit" className="px-4 py-2 bg-navy text-white text-sm">
              Save burden assignment
            </button>
          </form>
        </div>
      )}

      {tab === "holidays" && (
        <div className="space-y-4 rounded-xl border border-navy-100 bg-white p-5">
          <h2 className="font-semibold text-navy">Company holiday calendar</h2>
          <p className="text-sm text-ink-muted leading-relaxed">
            Choose inherit US federal holidays (then add or delete) or start empty and add only
            what your company observes. This calendar is separate from the{" "}
            <code className="text-navy">PT-HOLIDAY</code> charge code (owned by VP HR).
          </p>
          {holidayMsg && (
            <p className="rounded-lg bg-brand-teal/10 px-3 py-2 text-sm text-navy">{holidayMsg}</p>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={
                holidayMode === "inherit_federal"
                  ? "rounded-lg bg-navy-900 px-3 py-1.5 text-sm font-semibold text-white"
                  : "rounded-lg border border-navy-100 px-3 py-1.5 text-sm"
              }
              onClick={() => {
                setHolidayMode("inherit_federal");
                setHolidays((h) => mergeFederal(h, [2026, 2027]));
                setHolidayMsg("Federal holidays merged for 2026–2027 (existing company days kept).");
              }}
            >
              Inherit federal
            </button>
            <button
              type="button"
              className={
                holidayMode === "start_empty"
                  ? "rounded-lg bg-navy-900 px-3 py-1.5 text-sm font-semibold text-white"
                  : "rounded-lg border border-navy-100 px-3 py-1.5 text-sm"
              }
              onClick={() => {
                setHolidayMode("start_empty");
                setHolidays([]);
                setHolidayMsg("Calendar cleared — add company holidays below.");
              }}
            >
              Start empty
            </button>
            <button
              type="button"
              className="rounded-lg border border-navy-100 px-3 py-1.5 text-sm font-medium"
              onClick={() => {
                setHolidays((h) => mergeFederal(h, [2026, 2027]));
                setHolidayMsg("Re-imported missing federal dates (deleted federal days stay gone).");
              }}
            >
              Re-import missing federal
            </button>
          </div>
          <form
            className="flex flex-wrap items-end gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!newHol.date || !newHol.name.trim()) return;
              setHolidays((prev) =>
                [
                  ...prev.filter((h) => h.date !== newHol.date),
                  {
                    id: `co-${newHol.date}`,
                    date: newHol.date,
                    name: newHol.name.trim(),
                    source: "company" as const,
                  },
                ].sort((a, b) => a.date.localeCompare(b.date)),
              );
              setNewHol({ date: "", name: "" });
              setHolidayMsg("Company holiday added.");
            }}
          >
            <label className="text-xs">
              Date
              <input
                type="date"
                className="mt-1 block rounded-lg border border-navy-100 px-2 py-1.5 text-sm"
                value={newHol.date}
                onChange={(e) => setNewHol((f) => ({ ...f, date: e.target.value }))}
                required
              />
            </label>
            <label className="text-xs grow min-w-[12rem]">
              Name
              <input
                className="mt-1 block w-full rounded-lg border border-navy-100 px-2 py-1.5 text-sm"
                value={newHol.name}
                onChange={(e) => setNewHol((f) => ({ ...f, name: e.target.value }))}
                required
              />
            </label>
            <button
              type="submit"
              className="rounded-lg bg-navy-900 px-3 py-2 text-sm font-semibold text-white"
            >
              Add holiday
            </button>
          </form>
          <div className="overflow-x-auto rounded-lg border border-navy-100">
            <table className="w-full text-sm">
              <thead className="bg-navy-50 text-xs uppercase tracking-wide text-ink-subtle">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Name</th>
                  <th className="px-3 py-2 text-left">Source</th>
                  <th className="px-3 py-2 text-left"> </th>
                </tr>
              </thead>
              <tbody>
                {holidays.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-4 text-ink-muted">
                      No holidays — add your own or inherit federal.
                    </td>
                  </tr>
                ) : (
                  holidays.map((h) => (
                    <tr key={h.id} className="border-t border-navy-100">
                      <td className="px-3 py-2 font-mono text-xs">{h.date}</td>
                      <td className="px-3 py-2">{h.name}</td>
                      <td className="px-3 py-2 text-ink-muted">{h.source}</td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          className="text-xs font-semibold text-red-700"
                          onClick={() => {
                            setHolidays((prev) => prev.filter((x) => x.id !== h.id));
                            setHolidayMsg(`Removed ${h.name}.`);
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "leave" && (
        <div className="space-y-4">
          {waking && (
            <p className="text-sm text-slate bg-mist border border-black/5 px-3 py-2">
              Demo database is starting — leave policy edits use local cache until Aurora is ready.
            </p>
          )}
          {source === "api" && !waking && (
            <p className="text-xs text-slate">Leave policy synced to demo Aurora.</p>
          )}
          <div className="bg-white border border-black/5 p-4 space-y-3">
            <h2 className="font-semibold text-navy">Company fiscal year start</h2>
            <p className="text-xs text-slate">
              Leave charts align to this FY. Default for new companies is January 1 (calendar year).
            </p>
            <form
              className="flex flex-wrap items-end gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                const month = Math.min(12, Math.max(1, Number(fyForm.fy_start_month) || 1));
                const day = Math.min(28, Math.max(1, Number(fyForm.fy_start_day) || 1));
                void persistFy({
                  company_id: fy.company_id || "co-hfs",
                  fy_start_month: month,
                  fy_start_day: day,
                });
                setLeaveMsg(`FY start saved as ${month}/${day}.`);
              }}
            >
              <label className="text-sm font-medium text-navy">
                Month
                <input
                  type="number"
                  min={1}
                  max={12}
                  className="mt-1 block w-20 border border-black/10 px-3 py-2"
                  value={fyForm.fy_start_month}
                  onChange={(e) => setFyForm((f) => ({ ...f, fy_start_month: e.target.value }))}
                />
              </label>
              <label className="text-sm font-medium text-navy">
                Day
                <input
                  type="number"
                  min={1}
                  max={28}
                  className="mt-1 block w-20 border border-black/10 px-3 py-2"
                  value={fyForm.fy_start_day}
                  onChange={(e) => setFyForm((f) => ({ ...f, fy_start_day: e.target.value }))}
                />
              </label>
              <button type="submit" className="px-3 py-2 text-sm bg-navy text-white">
                Save FY start
              </button>
            </form>
          </div>
          <div className="bg-white border border-black/5 p-4 space-y-3">
            <h2 className="font-semibold text-navy">Leave mode</h2>
            <p className="text-xs text-slate">
              Switch to flex on an effective date to stop accrual charts and show utilization
              instead.
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={`px-3 py-1.5 text-sm ${
                  leavePolicy.mode === "accrual" ? "bg-teal text-white" : "border border-black/10"
                }`}
                onClick={() => {
                  const next = {
                    ...leavePolicy,
                    mode: "accrual" as const,
                    flex_effective_date: null as string | null,
                  };
                  void persistPolicy(next);
                  setLeaveMsg("Accrual mode saved.");
                }}
              >
                Accrual
              </button>
              <button
                type="button"
                className={`px-3 py-1.5 text-sm ${
                  leavePolicy.mode === "flex" || leavePolicy.mode === "unlimited"
                    ? "bg-teal text-white"
                    : "border border-black/10"
                }`}
                onClick={() => {
                  const next = {
                    ...leavePolicy,
                    mode: "flex" as const,
                    flex_effective_date:
                      leavePolicy.flex_effective_date ||
                      new Date().toISOString().slice(0, 10),
                  };
                  void persistPolicy(next);
                  setLeaveMsg("Flex leave mode saved — utilization after effective date.");
                }}
              >
                Flex time
              </button>
            </div>
            {(leavePolicy.mode === "flex" || leavePolicy.mode === "unlimited") && (
              <>
                <label className="block text-sm font-medium text-navy max-w-xs">
                  Flex effective date
                  <input
                    type="date"
                    className="mt-1 w-full border border-black/10 px-3 py-2"
                    value={leavePolicy.flex_effective_date ?? ""}
                    onChange={(e) => {
                      const next = {
                        ...leavePolicy,
                        mode: "flex" as const,
                        flex_effective_date: e.target.value || null,
                      };
                      void persistPolicy(next);
                    }}
                  />
                </label>
                <label className="block text-sm font-medium text-navy max-w-xs">
                  Billable utilization target (%)
                  <input
                    type="number"
                    min={0}
                    max={100}
                    className="mt-1 w-full border border-black/10 px-3 py-2"
                    value={leavePolicy.utilization_target_pct}
                    onChange={(e) => {
                      const next = {
                        ...leavePolicy,
                        utilization_target_pct: Number(e.target.value) || 0,
                      };
                      void persistPolicy(next);
                    }}
                  />
                </label>
              </>
            )}
          </div>

          {leavePolicy.mode === "accrual" && (
            <div className="grid lg:grid-cols-2 gap-4">
              <div className="bg-white border border-black/5 overflow-hidden">
                <div className="px-4 py-3 border-b border-black/5 font-semibold text-navy">
                  Tenure accrual schedule
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-mist">
                    <tr>
                      <th className="text-left px-3 py-2 text-slate">Years+</th>
                      <th className="text-left px-3 py-2 text-slate">Annual hrs</th>
                      <th className="text-left px-3 py-2 text-slate">Per pay</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...leavePolicy.accrual_bands]
                      .sort((a, b) => a.min_years - b.min_years)
                      .map((b) => (
                        <tr key={b.id} className="border-t border-black/5">
                          <td className="px-3 py-2">{b.min_years}</td>
                          <td className="px-3 py-2">{b.annual_hours}</td>
                          <td className="px-3 py-2">
                            {perPeriodAccrual(b.annual_hours, leavePolicy.pay_periods_per_year)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                <form
                  className="p-4 border-t border-black/5 space-y-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const band: LeaveAccrualBand = {
                      id: `band-${Date.now()}`,
                      min_years: Number(bandForm.min_years) || 0,
                      annual_hours: Number(bandForm.annual_hours) || 0,
                    };
                    const next = {
                      ...leavePolicy,
                      accrual_bands: [...leavePolicy.accrual_bands, band],
                    };
                    void persistPolicy(next);
                    setLeaveMsg("Accrual band saved. Anniversary switches rate by tenure.");
                    setBandForm({ min_years: "", annual_hours: "" });
                  }}
                >
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      className="border border-black/10 px-3 py-2 text-sm"
                      placeholder="Min years"
                      value={bandForm.min_years}
                      onChange={(e) => setBandForm((f) => ({ ...f, min_years: e.target.value }))}
                    />
                    <input
                      className="border border-black/10 px-3 py-2 text-sm"
                      placeholder="Annual hours"
                      value={bandForm.annual_hours}
                      onChange={(e) =>
                        setBandForm((f) => ({ ...f, annual_hours: e.target.value }))
                      }
                    />
                  </div>
                  <button type="submit" className="px-3 py-2 text-sm bg-navy text-white">
                    Add tenure band
                  </button>
                </form>
              </div>

              <div className="bg-white border border-black/5 overflow-hidden">
                <div className="px-4 py-3 border-b border-black/5 font-semibold text-navy">
                  Max carryover (effective-dated)
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-mist">
                    <tr>
                      <th className="text-left px-3 py-2 text-slate">Max hrs</th>
                      <th className="text-left px-3 py-2 text-slate">Effective</th>
                      <th className="text-left px-3 py-2 text-slate">End</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leavePolicy.carryover.map((c) => (
                      <tr key={c.id} className="border-t border-black/5">
                        <td className="px-3 py-2">{c.max_hours}</td>
                        <td className="px-3 py-2">{c.effective_date}</td>
                        <td className="px-3 py-2">{c.end_date ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <form
                  className="p-4 border-t border-black/5 space-y-2"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!carryForm.effective_date) return;
                    const next = {
                      ...leavePolicy,
                      carryover: [
                        {
                          id: `co-${Date.now()}`,
                          max_hours: Number(carryForm.max_hours) || 0,
                          effective_date: carryForm.effective_date,
                          end_date: null as string | null,
                        },
                        ...leavePolicy.carryover.map((c) =>
                          c.end_date == null && c.effective_date < carryForm.effective_date
                            ? {
                                ...c,
                                end_date: (() => {
                                  const d = new Date(carryForm.effective_date + "T12:00:00");
                                  d.setDate(d.getDate() - 1);
                                  return d.toISOString().slice(0, 10);
                                })(),
                              }
                            : c,
                        ),
                      ],
                    };
                    void persistPolicy(next);
                    setLeaveMsg("Carryover policy saved.");
                    setCarryForm({ max_hours: "40", effective_date: "" });
                  }}
                >
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      className="border border-black/10 px-3 py-2 text-sm"
                      placeholder="Max hours"
                      value={carryForm.max_hours}
                      onChange={(e) => setCarryForm((f) => ({ ...f, max_hours: e.target.value }))}
                    />
                    <input
                      type="date"
                      required
                      className="border border-black/10 px-3 py-2 text-sm"
                      value={carryForm.effective_date}
                      onChange={(e) =>
                        setCarryForm((f) => ({ ...f, effective_date: e.target.value }))
                      }
                    />
                  </div>
                  <button type="submit" className="px-3 py-2 text-sm bg-navy text-white">
                    Add carryover policy
                  </button>
                </form>
              </div>
            </div>
          )}
          {leaveMsg && <p className="text-sm text-teal">{leaveMsg}</p>}
        </div>
      )}
    </div>
  );
}
